/**
 * Runtime Environment Configuration Loader
 * 
 * Loads and merges environment configuration from multiple sources:
 * - Build-time environment files
 * - Runtime environment variables
 * - External configuration files
 * - Server-side configuration APIs
 */

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, combineLatest } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';

import { Environment, RuntimeEnvironment, EnvironmentVariables } from './environment.interface';
import { EnvironmentValidator } from './environment-validator';
import { MOCK_FLAG_DEFINITIONS, MOCK_FLAG_KEYS } from '@core/config/mock-flag-definitions';

declare const window: any;

/** Keys whose values are redacted in cached environment data to limit exposure in browser storage.
 * The keys remain present (preserving the type contract) but values are replaced with safe defaults. */
const CACHE_REDACTED_SECTIONS = ['auth', 'security', 'sentry'] as const;

@Injectable({
  providedIn: 'root'
})
export class EnvironmentLoader {
  private loadedEnvironment?: RuntimeEnvironment;
  private configCache = new Map<string, unknown>();

  constructor(private http: HttpClient) {}

  /**
   * Loads the complete runtime environment configuration
   */
  loadEnvironment(baseEnvironment: Environment): Observable<RuntimeEnvironment> {
    if (this.loadedEnvironment) {
      return of(this.loadedEnvironment);
    }

    return combineLatest([
      this.loadEnvironmentVariables(),
      this.loadExternalConfig(),
      this.loadServerConfig()
    ]).pipe(
      map(([envVars, externalConfig, serverConfig]) => {
        const runtimeEnv = this.mergeConfigurations(
          baseEnvironment,
          envVars,
          externalConfig,
          serverConfig
        );

        // Validate the merged configuration
        const validation = EnvironmentValidator.validate(runtimeEnv);

        // In development mode, treat validation errors as warnings and continue
        // In production mode, validation errors should throw
        if (!validation.valid) {
          if (runtimeEnv.production || runtimeEnv.staging) {
            // Do not log validation details to console in deployed environments
            throw new Error('Invalid runtime environment configuration');
          } else {
            // eslint-disable-next-line no-console
            console.warn('Runtime environment validation issues (non-blocking in development):');
            // eslint-disable-next-line no-console
            validation.errors.forEach(error => console.warn(`  - ${error}`));
          }
        }

        if (validation.warnings.length > 0 && !runtimeEnv.production && !runtimeEnv.staging) {
          // eslint-disable-next-line no-console
          console.warn('Runtime environment warnings:', validation.warnings);
        }

        this.loadedEnvironment = runtimeEnv;
        this.cacheEnvironment(runtimeEnv);

        if (!runtimeEnv.production && !runtimeEnv.staging) {
          // eslint-disable-next-line no-console
          console.log('Runtime environment loaded successfully');
        }
        return runtimeEnv;
      }),
      catchError(error => {
        if (!baseEnvironment.production && !baseEnvironment.staging) {
          // eslint-disable-next-line no-console
          console.error('Failed to load runtime environment:', error);
        }
        // Fallback to base environment
        const fallbackEnv = baseEnvironment as RuntimeEnvironment;
        this.loadedEnvironment = fallbackEnv;
        return of(fallbackEnv);
      })
    );
  }

  /**
   * Gets the currently loaded environment
   */
  getCurrentEnvironment(): RuntimeEnvironment | undefined {
    return this.loadedEnvironment;
  }

  /**
   * Reloads the environment configuration
   */
  reloadEnvironment(baseEnvironment: Environment): Observable<RuntimeEnvironment> {
    this.loadedEnvironment = undefined;
    this.configCache.clear();
    return this.loadEnvironment(baseEnvironment);
  }

  /**
   * Loads environment variables from window object (injected by server)
   */
  private loadEnvironmentVariables(): Observable<Partial<Environment>> {
    return of(this.processEnvironmentVariables());
  }

  /**
   * Loads external configuration from JSON files
   */
  private loadExternalConfig(): Observable<Partial<Environment>> {
    const configUrl = '/assets/config/runtime-config.json';
    
    return this.http.get<any>(configUrl).pipe(
      tap(config => {
        this.configCache.set('external', config);
      }),
      catchError(() => {
        return of({});
      })
    );
  }

  /**
   * Loads server-side configuration via API
   */
  private loadServerConfig(): Observable<Partial<Environment>> {
    // Only attempt to load server config if we have a base API URL
    const baseApiUrl = this.getBaseApiUrl();
    if (!baseApiUrl) {
      return of({});
    }

    const serverConfigUrl = `${baseApiUrl}/config/environment`;
    
    return this.http.get<any>(serverConfigUrl).pipe(
      tap(config => {
        this.configCache.set('server', config);
      }),
      catchError(() => {
        return of({});
      })
    );
  }

  /**
   * Processes environment variables from the window object
   */
  private processEnvironmentVariables(): Partial<Environment> {
    const envVars: EnvironmentVariables = window.__RAZOR_ENV__ || {};
    
    if (Object.keys(envVars).length === 0) {
      return {};
    }

    const validation = EnvironmentValidator.validateEnvironmentVariables(envVars);

    return validation.processed;
  }

  /**
   * Merges configurations from multiple sources with proper precedence
   */
  private mergeConfigurations(
    base: Environment,
    envVars: Partial<Environment>,
    external: Partial<Environment>,
    server: Partial<Environment>
  ): RuntimeEnvironment {
    // Precedence (highest to lowest):
    // 1. Environment variables (runtime)
    // 2. Server configuration
    // 3. External configuration files
    // 4. Base environment

    const merged = this.deepMerge(
      base,
      external,
      server,
      envVars
    ) as RuntimeEnvironment;

    // Add runtime-specific properties
    merged.buildTime = new Date().toISOString();
    
    // Add deployment information if available
    const deploymentInfo = this.getDeploymentInfo();
    Object.assign(merged, deploymentInfo);

    return merged;
  }

  /**
   * Deep merges multiple objects
   * Only overwrites values if the new value is not undefined/null/empty string
   * First object is used as the base and deeply cloned
   */
  private deepMerge(...objects: unknown[]): unknown {
    if (objects.length === 0) return {};

    // Deep clone the first object to avoid mutating it
    const result = JSON.parse(JSON.stringify(objects[0] || {}));

    // Merge remaining objects into the cloned base
    for (let i = 1; i < objects.length; i++) {
      this.mergeInto(result, objects[i]);
    }

    return result;
  }

  /**
   * Security-critical fields that cannot be overridden by runtime configuration (SEC-002).
   * These are set at build time and must not be mutatable via window.__RAZOR_ENV__,
   * runtime-config.json, or server config API.
   */
  private static readonly FROZEN_FIELDS = new Set(['production', 'staging']);

  /**
   * Nested keys frozen against runtime override, keyed by parent section name.
   * GAP-FE-011: sentry.dsn and sentry.enabled are frozen because a runtime-injected
   * DSN would redirect error telemetry (including breadcrumbs and session replays)
   * to an attacker-controlled Sentry project. The sanctioned injection channel
   * (inject-env.sh ENVSUBST allowlist) intentionally excludes SENTRY_*.
   */
  private static readonly FROZEN_NESTED_KEYS = new Map<string, ReadonlySet<string>>([
    ['sentry', new Set(['dsn', 'enabled'])],
  ]);

  /**
   * Feature flag keys that control mock data activation. Frozen against runtime override
   * to prevent mock data from being enabled in production via configuration injection.
   * GAP-FE-033: Derived from the centralized MOCK_FLAG_KEYS set to prevent drift.
   * GAP-FE-058: Also freezes dev/debug tool flags to prevent runtime enablement.
   */
  private static readonly FROZEN_FEATURE_KEYS: ReadonlySet<string> = new Set([
    ...MOCK_FLAG_KEYS,
    'enableDevTools',
    'enableDebugPanel',
  ]);

  /**
   * Recursively merges source object into target.
   * Skips frozen fields and frozen feature keys to prevent runtime override of
   * security-critical configuration.
   */
  private mergeInto(target: any, source: any, parentKey?: string): void {
    if (!source || typeof source !== 'object') return;

    Object.keys(source).forEach(key => {
      // Block runtime override of security-critical top-level fields
      if (!parentKey && EnvironmentLoader.FROZEN_FIELDS.has(key)) {
        return;
      }

      // Block runtime override of mock feature flags
      if (parentKey === 'features' && EnvironmentLoader.FROZEN_FEATURE_KEYS.has(key)) {
        return;
      }

      // Block runtime override of frozen nested keys (e.g. sentry.dsn, sentry.enabled)
      if (parentKey && EnvironmentLoader.FROZEN_NESTED_KEYS.has(parentKey)) {
        const frozenKeys = EnvironmentLoader.FROZEN_NESTED_KEYS.get(parentKey)!;
        if (frozenKeys.has(key)) {
          return;
        }
      }

      const sourceVal = source[key];
      const targetVal = target[key];

      // Skip undefined, null, or empty string values - don't overwrite existing values
      if (sourceVal === undefined || sourceVal === null || sourceVal === '') {
        return;
      }

      if (Array.isArray(targetVal) && Array.isArray(sourceVal)) {
        target[key] = [...targetVal, ...sourceVal];
      } else if (targetVal && sourceVal && typeof targetVal === 'object' && typeof sourceVal === 'object' && !Array.isArray(sourceVal)) {
        this.mergeInto(targetVal, sourceVal, key);
      } else {
        target[key] = sourceVal;
      }
    });
  }

  /**
   * Gets deployment information from environment
   */
  private getDeploymentInfo(): Partial<RuntimeEnvironment> {
    const envVars: EnvironmentVariables = window.__RAZOR_ENV__ || {};
    
    return {
      deploymentId: envVars.DEPLOYMENT_ID,
      region: envVars.DEPLOYMENT_REGION,
      cluster: envVars.DEPLOYMENT_CLUSTER
    };
  }

  /**
   * Gets base API URL from various sources
   */
  private getBaseApiUrl(): string | null {
    // Try environment variables first
    const envVars: EnvironmentVariables = window.__RAZOR_ENV__ || {};
    if (envVars.RAZOR_API_URL) {
      return envVars.RAZOR_API_URL;
    }

    // Try current location
    if (window?.location?.origin) {
      return `${window.location.origin}/api/v1`;
    }

    return null;
  }

  /**
   * Caches the environment configuration
   */
  private cacheEnvironment(env: RuntimeEnvironment): void {
    try {
      // Redact sensitive config sections before caching to browser storage.
      // An XSS payload or malicious extension could read localStorage/sessionStorage.
      // We keep the keys (preserving the RuntimeEnvironment type contract) but replace
      // values with empty/safe defaults so callers don't crash on missing properties.
      const safeCopy = JSON.parse(JSON.stringify(env)) as Record<string, unknown>;
      for (const key of CACHE_REDACTED_SECTIONS) {
        if (safeCopy[key] && typeof safeCopy[key] === 'object') {
          const section = safeCopy[key] as Record<string, unknown>;
          for (const subKey of Object.keys(section)) {
            if (typeof section[subKey] === 'string') {
              section[subKey] = '';
            } else if (typeof section[subKey] === 'boolean') {
              section[subKey] = false;
            } else if (typeof section[subKey] === 'number') {
              section[subKey] = 0;
            }
          }
        }
      }

      // GAP-FE-033: Force all mock feature flags to false in cached environment.
      // Prevents XSS or stale cache from enabling mock data when the cached
      // environment is loaded without re-running EnvironmentValidator.validate().
      const features = safeCopy['features'] as Record<string, unknown> | undefined;
      if (features) {
        for (const flagKey of EnvironmentLoader.FROZEN_FEATURE_KEYS) {
          features[flagKey] = false;
        }
      }

      // Cache in sessionStorage for this session
      sessionStorage.setItem('razor-environment', JSON.stringify({
        timestamp: Date.now(),
        config: safeCopy
      }));

      // Also cache in localStorage with expiration
      const cacheData = {
        timestamp: Date.now(),
        expiry: Date.now() + (24 * 60 * 60 * 1000), // 24 hours
        config: safeCopy
      };
      localStorage.setItem('razor-environment-cache', JSON.stringify(cacheData));
    } catch {
      // Storage unavailable (private browsing, quota exceeded) — non-fatal
    }
  }

  /**
   * Loads cached environment configuration
   * Only returns cache if it matches the expected environment mode (dev/prod)
   */
  loadCachedEnvironment(expectedProduction?: boolean): RuntimeEnvironment | null {
    try {
      // Try session storage first
      const sessionData = sessionStorage.getItem('razor-environment');
      if (sessionData) {
        const parsed = JSON.parse(sessionData);
        const cachedConfig = parsed.config as RuntimeEnvironment;

        // Validate cached environment mode matches expected mode
        if (expectedProduction !== undefined && cachedConfig.production !== expectedProduction) {
          sessionStorage.removeItem('razor-environment');
          return null;
        }

        return cachedConfig;
      }

      // Try localStorage with expiration check
      const localData = localStorage.getItem('razor-environment-cache');
      if (localData) {
        const parsed = JSON.parse(localData);
        if (parsed.expiry > Date.now()) {
          const cachedConfig = parsed.config as RuntimeEnvironment;

          // Validate cached environment mode matches expected mode
          if (expectedProduction !== undefined && cachedConfig.production !== expectedProduction) {
            localStorage.removeItem('razor-environment-cache');
            return null;
          }

          return cachedConfig;
        } else {
          localStorage.removeItem('razor-environment-cache');
        }
      }
    } catch {
      // Storage unavailable — non-fatal
    }

    return null;
  }

  /**
   * Clears all cached environment data
   */
  clearCache(): void {
    try {
      sessionStorage.removeItem('razor-environment');
      localStorage.removeItem('razor-environment-cache');
      this.configCache.clear();
    } catch {
      // Storage unavailable — non-fatal
    }
  }
}

/**
 * Factory function for loading environment at app startup
 */
export function createEnvironmentLoader(
  http: HttpClient,
  baseEnvironment: Environment
): () => Promise<RuntimeEnvironment> {
  return () => {
    const loader = new EnvironmentLoader(http);

    // Try to load from cache first, but only if it matches expected environment mode
    const cached = loader.loadCachedEnvironment(baseEnvironment.production);
    if (cached) {
      return Promise.resolve(cached);
    }

    // Load fresh configuration
    return loader.loadEnvironment(baseEnvironment).toPromise() as Promise<RuntimeEnvironment>;
  };
}
